import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '../../backend/useUser';
import { useCredits } from '../../backend/useCredits';
import { useConfig } from '../../utils/config';
import UpsellModal from './UpsellModal';
import { useCreditsContext } from './CreditsContext';

const UPSELL_POPUP_FREQUENCY = 2 * 60 * 1000; // 2 minutes in milliseconds

interface UpsellModalTriggerProps {
  colorMode?: 'light' | 'dark';
}

const UpsellModalTrigger = ({ colorMode = 'dark' }: UpsellModalTriggerProps) => {
  const { user, loading: userLoading } = useUser();
  const { getSubscription } = useCredits();
  const config = useConfig();
  const location = useLocation();
  const { setIsUpsellModalOpen, subscription } = useCreditsContext();
  const [isOpen, setIsOpen] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    const checkEligibility = async () => {
      // Only trigger on root /game and /art routes
      // We check for exact match or trailing slash
      const path = location.pathname.replace(/\/$/, '');
      if (path !== '/game' && path !== '/art' && path !== '/animate') return;

      // Wait for user to load
      if (userLoading) return;
      
      // If not logged in, do nothing
      if (!user) return;

      // Avoid re-checking if we already checked/opened
      if (hasChecked && (subscription !== 'free' && subscription !== null)) {
        setIsOpen(false);
        return;
      }

      try {
        const lastSeen = localStorage.getItem('upsell_last_seen');
        const now = Date.now();

        // If lastSeen is null, this block is skipped correctly
        if (lastSeen) {
          const lastSeenTime = parseInt(lastSeen, 10);
          if (now - lastSeenTime < UPSELL_POPUP_FREQUENCY) {
            setHasChecked(true);
            return;
          }
        }

        const currentSubscription = await getSubscription(user.id);
        // Trigger if subscription is null, undefined, or 'free'
        if (!currentSubscription || currentSubscription === 'free') {
          setIsOpen(true);
          localStorage.setItem('upsell_last_seen', now.toString());
        } else {
          setIsOpen(false);
        }
      } catch (error) {
        console.error("Error checking subscription for upsell trigger:", error);
      } finally {
        setHasChecked(true);
      }
    };

    checkEligibility();
  }, [user, userLoading, config.ENABLE_TRIAL, getSubscription, hasChecked, location.pathname, subscription]);

  // Sync modal state with CreditsContext
  useEffect(() => {
    setIsUpsellModalOpen(isOpen);
  }, [isOpen, setIsUpsellModalOpen]);

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <UpsellModal 
      isOpen={isOpen} 
      onClose={handleClose} 
      colorMode={colorMode} 
    />
  );
};

export default UpsellModalTrigger;